import React, { useState, useEffect } from "react";
import { ImageModifier } from "../../../common/ImageModifier";
import { TextModifier } from "../../../common/TextModifier";
import {
    Typography,
    Box,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControlLabel,
    Autocomplete,
    TextField,
    Stack,
    Divider,
    Button,
    Grid,
    ListItem
} from "@mui/material";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { usePermissionContext } from "../../../../contexts/ProviderProvider";
import { TeamImageModifier } from "./TeamImageModifier";

export const NewTeamProductEQMConfig = ({
    universities,
    eqmConfig,
    setEQMConfig,
    rows,
    setRows,
    errorMessage,
    setErrorMessage,
    expanded,
    setExpanded,
    // Updated props for asset options - now arrays of objects
    fontOptions,
    backIconLinkOptions,
    frontIconLinkOptions,
    templateLinkOptions,
    compositeOptions,
    generalAssetByType,
    selectedSkuType
}) => {

    const { views } = usePermissionContext();

    // State for conditional fields per university
    const [conditionalStates, setConditionalStates] = useState({});

    // Updated field order and mapping based on reference code
    const fieldOrder = [
        "has_front_asset", // Conditional trigger
        "has_back_asset", // Conditional trigger
        "has_mockup_asset", // Conditional trigger
    ];

    const getAssetTypeByfieldId = (name) => {
        if (name === "front_icon_general_asset_id") {
            return "Front Asset Icon File";
        } else if (name === "back_icon_general_asset_id") {
            return "Back Asset Icon File";
        } else if (name === "mockup_template_general_asset_id") {
            return selectedSkuType === "Jersey" ? "Jersey Art File" : "Mockup Template File";
        } else if (name === "front_text_font_link_general_asset_id" || name === "back_text_font_general_asset_id") {
            return "Font File";
        } else if (name === "back_jersey_number_font_general_asset_id") {
            return "Composite Font File";
        } else {
            return null;
        }
    }

    const getThumbnailVariantForImage = (img) => {
        if (img.includes("imagedelivery")) {
            return img.replace("/original", "/thumbnail");
        }
        return img
    }

    const isValidHexCode = (hexCode) => {
        if (!hexCode) return false;
        const hexRegex = /^#?([0-9A-F]{3}){1,2}$/i;
        return hexRegex.test(hexCode);
    };

    const setEQMConfigFilter = (university, value, field) => {
        setErrorMessage("");
        setEQMConfig((prev) => {
            const newState = { ...prev };
            if (!newState[university]) {
                newState[university] = {
                    "has_mockup_asset": true,
                    "has_front_asset": true,
                };
            }
            if (value !== undefined && value !== null && value !== "") {
                newState[university] = { ...newState[university], [field]: value };
            } else {
                delete newState[university][field];
                if (Object.keys(newState[university]).length === 0) {
                    delete newState[university];
                }
            }
            return newState;
        });
    };

    // Helper function to get options array for a specific field
    const getOptionsForField = (field) => {
        if (field === "front_icon_general_asset_id") {
            return frontIconLinkOptions || [];
        } else if (field === "back_icon_general_asset_id") {
            return backIconLinkOptions || [];
        } else if (field === "mockup_template_general_asset_id") {
            return templateLinkOptions || [];
        } else if (field === "front_text_font_link_general_asset_id" || field === "back_text_font_general_asset_id") {
            return fontOptions || [];
        } else if (field === "back_jersey_number_font_general_asset_id") {
            return compositeOptions || [];
        }
        return [];
    };

    // Updated function to handle the new data structure with object arrays
    const getAssetIdFromName = (field, name, generalAssetByType, selectedSkuType) => {
        if (!name || !generalAssetByType) return null;

        let assetType = "";
        if (field === "front_icon_general_asset_id") {
            assetType = "Front Asset Icon File";
        } else if (field === "back_icon_general_asset_id") {
            assetType = "Back Asset Icon File";
        } else if (field === "mockup_template_general_asset_id") {
            assetType = selectedSkuType === "Jersey" ? "Jersey Art File" : "Mockup Template File";
        } else if (
            field === "front_text_font_link_general_asset_id" ||
            field === "back_text_font_general_asset_id"
        ) {
            assetType = "Font File";
        } else if (field === "back_jersey_number_font_general_asset_id") {
            assetType = "Composite Font File";
        }

        const assetMap = generalAssetByType?.[assetType];
        if (!assetMap) return null;

        // Find the asset by name in the integer-keyed map
        const assetEntry = Object.values(assetMap).find(
            (obj) => obj.name === name
        );

        return assetEntry?.idProductGeneralAssets || null;
    };

    // Updated function to get name from asset ID
    const getNameFromAssetId = (field, id, generalAssetByType, selectedSkuType) => {
        if (!id || !generalAssetByType) return "";

        const assetType = getAssetTypeByfieldId(field);
        const assetMap = generalAssetByType?.[assetType];
        if (!assetMap) return "";

        // Find the asset by ID in the integer-keyed map
        const assetEntry = Object.values(assetMap).find(
            (obj) => obj.idProductGeneralAssets === id
        );

        return assetEntry?.name || "";
    };

    // Returns request body similar to ConfigDetailedView
    const getEQMRequestBody = (eqmConfig, universities, rows, conditionalStates) => {
        const body = {};

        universities.forEach((university) => {
            const config = eqmConfig[university] || {};
            const conditional = conditionalStates[university] || {};

            const universityData = {};

            rows.forEach((row) => {
                const fieldValue = config[row.name];
                if (fieldValue !== undefined && fieldValue !== "") {
                    universityData[row.name] = fieldValue;
                }
            });

            // Add conditional flags
            universityData["has_front_sport_text"] = conditional.has_front_asset || false;
            universityData["has_back_asset_icon"] = conditional.has_back_asset || false;
            universityData["has_mockup_template_link"] = conditional.has_mockup_asset || false;

            body[university] = universityData;
        });

        return body;
    };

    const setConditionalState = (university, field, value) => {
        setConditionalStates(prev => ({
            ...prev,
            [university]: {
                ...prev[university],
                [field]: value
            }
        }));

        setEQMConfigFilter(university, value, field);
    };

    const getConditionalState = (university, field) => {
        return conditionalStates[university]?.[field] || false;
    };

    const getColor = (university, row) => {
        const universityConfig = eqmConfig[university] || {};
        const colorText = universityConfig[row.name] !== undefined ? universityConfig[row.name] : row.value;
        if (!colorText) return
        return colorText.startsWith("#") ? colorText : "#" + colorText;
    };

    // Updated helper function to get URL for a given field and value
    const getCurrentImageURL = (university, fieldName) => {
        const universityConfig = eqmConfig[university] || {};
        const currentValue = universityConfig[fieldName] || "";

        if (currentValue) {
            const url = getURLForField(fieldName, currentValue);
            return url.replace("https://www.dropbox.com", "https://dl.dropboxusercontent.com");
        }

        return "";
    };

    // Updated function to handle the new data structure
    const getURLForField = (field, id) => {
        if (!id || !generalAssetByType) return "";

        const assetType = getAssetTypeByfieldId(field);
        const assetMap = generalAssetByType[assetType];

        if (!assetMap) return "";

        // Find the asset by ID in the integer-keyed map
        const assetEntry = Object.values(assetMap).find(
            (obj) => obj.idProductGeneralAssets === id
        );

        return assetEntry?.asset_url || "";
    };

    // Updated config options to match reference structure
    const configOptions = [
        { "label": "Front Asset", "type": "Boolean", "required": false, "name": "has_front_asset", "position": 14, "associatedRow": "front_asset_file", "title": "Front Asset Configuration" },
        { "label": "Front Asset File", "type": "File", "required": false, "name": "front_asset_file", "position": 15, "associatedRow": "has_front_asset" },
        { "label": "Back Asset", "type": "Boolean", "required": false, "name": "has_back_asset", "position": 16, "associatedRow": "back_asset_file", "title": "Back Asset Configuration" },
        { "label": "Template File Link", "type": "File", "required": false, "name": "back_asset_file", "position": 17, "associatedRow": "has_back_asset" },
        { "label": "Mockup", "type": "Boolean", "required": false, "name": "has_mockup_asset", "position": 18, "associatedRow": "mockup_asset_file", "title": "Mockup Configuration" },
        { "label": "Mockup File", "type": "File", "required": false, "name": "mockup_asset_file", "position": 19, "associatedRow": "has_mockup_asset" },
    ];

    const getSectionTitle = (fieldKey) => {
        if (fieldKey === "front_icon_general_asset_id") return "Front Asset Configuration";
        if (fieldKey === "back_text_font_color") return "Back Asset Configuration";
        if (fieldKey === "has_mockup_template") return "Mockup Configuration";
        return null;
    };

    useEffect(() => {
        const tempSelectedRow = configOptions.map((option) => ({
            name: option.name || '',
            label: option.label || '',
            type: option.type || '',
            value: "",
            required: option.required || false,
            position: option.position || 0,
            associatedRow: option.associatedRow || '',
            title: option.title || '',
        }));
        setRows(tempSelectedRow);
    }, []);

    // Initialize conditional states for universities
    useEffect(() => {
        const initialStates = {};
        universities.forEach(university => {
            const universityConfig = eqmConfig[university] || {};
            initialStates[university] = {
                has_front_asset: true,
                has_back_asset: !!universityConfig.has_back_asset,
                has_mockup_asset: true
            };
        });
        setConditionalStates(initialStates);
    }, [universities, eqmConfig]);

    const handleAccordionChange = (university) => () => {
        setExpanded((prev) => ({
            ...prev,
            [university]: !prev[university],
        }));
    };

    const renderColorField = (university, row) => (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                    <TextModifier
                        label={row.label}
                        original={row.value}
                        value={(eqmConfig[university] || {})[row.name]}
                        setValue={(value) => {
                            setEQMConfigFilter(university, value, row.name);
                        }}
                        permissions={[views.EQMANAGER]}
                        required={row.required && !!expanded[university]}
                        name={`${university}-${row.name}`}
                    />
                </Box>
                {getColor(university, row) && (
                    <Box
                        sx={{
                            bgcolor: getColor(university, row),
                            width: 60,
                            height: 40,
                            border: isValidHexCode(getColor(university, row)) ? "2px solid" : "2px dashed",
                            borderRadius: 1,
                            flexShrink: 0,
                        }}
                    />
                )}
            </Box>
        </Box>
    );

    const renderTextField = (university, row) => {
        const currentId = (eqmConfig[university] || {})[row.name];
        const currentValue = getNameFromAssetId(row.name, currentId, generalAssetByType, selectedSkuType);
        const options = getOptionsForField(row.name);

        return (
            <Box sx={{ mb: 2 }}>
                <Autocomplete
                    freeSolo
                    options={options}
                    getOptionLabel={(option) => {
                        // Handle both string and object options
                        if (typeof option === 'string') {
                            return option;
                        }
                        return option?.tags || '';
                    }}
                    value={currentValue}
                    onInputChange={(event, newValue) => {
                        const assetId = getAssetIdFromName(row.name, newValue, generalAssetByType, selectedSkuType);
                        setEQMConfigFilter(university, assetId, row.name);
                    }}
                    onChange={(event, newValue) => {
                        // Handle object selection
                        if (newValue && typeof newValue === 'object') {
                            setEQMConfigFilter(university, newValue.idProductGeneralAssets, row.name);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={row.label}
                            variant="outlined"
                            required={row.required && !!expanded[university]}
                            fullWidth
                            name={`${university}-${row.name}`}
                        />
                    )}
                    renderOption={(props, option) => (
                        <ListItem
                            {...props}
                            key={option.idProductGeneralAssets}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '2fr 3fr 75px',
                                alignItems: 'center',
                                paddingY: 1,
                                paddingX: 2,
                                gap: 2,
                                minHeight: '56px',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {option.name}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                                {option.tags}
                            </Typography>
                        </ListItem>
                    )}
                />

            </Box>
        );
    };

    const renderCompositeTextField = (university, row) => {
        const currentId = (eqmConfig[university] || {})[row.name];
        const currentValue = getNameFromAssetId(row.name, currentId, generalAssetByType, selectedSkuType);
        const imageURL = getCurrentImageURL(university, row.name);
        const options = getOptionsForField(row.name);

        return (
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Autocomplete
                            freeSolo
                            options={options}
                            getOptionLabel={(option) => {
                                // Handle both string and object options
                                if (typeof option === 'string') {
                                    return option;
                                }
                                return option?.tags || '';
                            }}
                            value={currentValue}
                            onInputChange={(event, newValue) => {
                                const assetId = getAssetIdFromName(row.name, newValue, generalAssetByType, selectedSkuType);
                                setEQMConfigFilter(university, assetId, row.name);
                            }}
                            onChange={(event, newValue) => {
                                // Handle object selection
                                if (newValue && typeof newValue === 'object') {
                                    setEQMConfigFilter(university, newValue.idProductGeneralAssets, row.name);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={row.label}
                                    variant="outlined"
                                    required={row.required && !!expanded[university]}
                                    fullWidth
                                    name={`${university}-${row.name}`}
                                />
                            )}
                            renderOption={(props, option) => (
                                <ListItem
                                    {...props}
                                    key={option.idProductGeneralAssets}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '2fr 3fr 75px',
                                        alignItems: 'center',
                                        paddingY: 1,
                                        paddingX: 2,
                                        gap: 2,
                                        minHeight: '56px',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {option.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {option.tags}
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            src={getThumbnailVariantForImage(option.asset_url)}
                                            alt="asset"
                                            style={{
                                                height: '50px',
                                                objectFit: 'contain',
                                                border: '1px solid #ccc',
                                                borderRadius: '2px',
                                                padding: '2px',
                                                background: '#fff',
                                            }}
                                        />
                                    </Box>
                                </ListItem>
                            )}

                        />
                    </Box>
                    {imageURL && (
                        <Box sx={{ flexShrink: 0 }}>
                            <img
                                alt="Preview"
                                style={{
                                    height: "56px",
                                    width: "80px",
                                    objectFit: "contain",
                                    cursor: "pointer",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    padding: "4px"
                                }}
                                src={imageURL}
                                onClick={() => window.open(imageURL, "_blank")}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const renderImageField = (university, row) => {
        const currentId = (eqmConfig[university] || {})[row.name];
        const currentValue = getNameFromAssetId(row.name, currentId, generalAssetByType, selectedSkuType);
        const imageURL = getCurrentImageURL(university, row.name);
        const options = getOptionsForField(row.name);

        return (
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Autocomplete
                            freeSolo
                            options={options}
                            getOptionLabel={(option) => {
                                // Handle both string and object options
                                if (typeof option === 'string') {
                                    return option;
                                }
                                return option?.tags || '';
                            }}
                            value={currentValue}
                            onInputChange={(event, newValue) => {
                                const assetId = getAssetIdFromName(row.name, newValue, generalAssetByType, selectedSkuType);
                                setEQMConfigFilter(university, assetId, row.name);
                            }}
                            onChange={(event, newValue) => {
                                // Handle object selection
                                if (newValue && typeof newValue === 'object') {
                                    setEQMConfigFilter(university, newValue.idProductGeneralAssets, row.name);
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={row.label}
                                    variant="outlined"
                                    required={row.required && !!expanded[university]}
                                    fullWidth
                                    name={`${university}-${row.name}`}
                                />
                            )}
                            renderOption={(props, option) => (
                                <ListItem
                                    {...props}
                                    key={option.idProductGeneralAssets}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '2fr 3fr 75px',
                                        alignItems: 'center',
                                        paddingY: 1,
                                        paddingX: 2,
                                        gap: 2,
                                        minHeight: '56px',
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {option.name}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                    >
                                        {option.tags}
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        <img
                                            src={getThumbnailVariantForImage(option.asset_url)}
                                            alt="asset"
                                            style={{
                                                height: '50px',
                                                objectFit: 'contain',
                                                border: '1px solid #ccc',
                                                borderRadius: '2px',
                                                padding: '2px',
                                                background: '#fff',
                                            }}
                                        />
                                    </Box>
                                </ListItem>
                            )}
                        />
                    </Box>
                    {imageURL && (
                        <Box sx={{ flexShrink: 0 }}>
                            <img
                                alt="Preview"
                                style={{
                                    height: "56px",
                                    width: "80px",
                                    objectFit: "contain",
                                    cursor: "pointer",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    padding: "4px"
                                }}
                                src={imageURL}
                                onClick={() => window.open(imageURL, "_blank")}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const renderBooleanField = (university, row) => {
        const universityConfig = eqmConfig[university] || {};
        const currentValue = universityConfig[row.name] !== undefined ? universityConfig[row.name] : row.value;

        return (
            <Box sx={{ mb: 2 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={Boolean(currentValue)}
                            onChange={(e) => {
                                setEQMConfigFilter(university, e.target.checked, row.name);
                            }}
                            name={`${university}-${row.name}`}
                        />
                    }
                    label={row.label}
                />
            </Box>
        );
    };

    const renderField = (university, row) => {
        // Skip conditional fields that are handled separately
        if (["front_text_font_link_general_asset_id", "front_text_font_color", "back_icon_general_asset_id", "mockup_template_general_asset_id"].includes(row.name)) {
            return null;
        }

        if (row.type === "Color") {
            return renderColorField(university, row);
        } else if (row.type === "Text" || row.type === "CompositeFontText") {
            return renderTextField(university, row);
        } else if (row.type === "CompositeText") {
            return renderCompositeTextField(university, row);
        } else if (row.type === "ImageText") {
            return renderImageField(university, row);
        } else if (row.type === "Boolean") {
            return renderBooleanField(university, row);
        } else {
            return (
                <Box sx={{ mb: 2 }}>
                    <ImageModifier
                        label={row.label}
                        original={row.name}
                        value={(eqmConfig[university] || {})[row.label]}
                        setValue={(value) => {
                            setEQMConfigFilter(university, value, row.name);
                        }}
                        name={`${university}-${row.name}`}
                        permissions={[views.EQMANAGER]}
                    />
                </Box>
            );
        }
    };

    const renderConditionalSection = (university, row) => {
        if (row.name === "has_front_asset") {
            const hasSportText = getConditionalState(university, "has_front_asset");
            const universityConfig = eqmConfig[university] || {};

            const currentFrontIconId = (eqmConfig[university] || {})["front_text_font_link_general_asset_id"];
            const currentFrontIconValue = getNameFromAssetId("front_text_font_link_general_asset_id", currentFrontIconId, generalAssetByType, selectedSkuType);

            return (
                <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={true}
                                disabled
                            // checked={hasSportText}
                            // onChange={(e) => {
                            //     setConditionalState(university, "has_front_asset", e.target.checked);
                            // }}
                            />
                        }
                        label="Has Front Asset?"
                    />
                    {/* {hasSportText && ( */}
                    <Box sx={{ ml: 3, mt: 1.5, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <Stack spacing={2}>
                            <TeamImageModifier
                                label={row.label + " file"}
                                original={row.associatedRow}
                                value={(eqmConfig[university] || {})[row.associatedRow]}
                                setValue={(value) => {
                                    setEQMConfigFilter(university, value, row.associatedRow);
                                }}
                                name={`${university}-${row.associatedRow}`}
                                permissions={[views.EQMANAGER]}
                            />
                        </Stack>
                    </Box>
                    {/* )} */}
                </Box>
            );
        } else if (row.name === "has_back_asset") {
            const hasBackIcon = getConditionalState(university, "has_back_asset");
            const universityConfig = eqmConfig[university] || {};
            const currentBackIconId = (eqmConfig[university] || {})["back_icon_general_asset_id"];
            const currentBackIconValue = getNameFromAssetId("back_icon_general_asset_id", currentBackIconId, generalAssetByType, selectedSkuType);

            return (
                <Box sx={{ mb: 4 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={hasBackIcon}
                                onChange={(e) => {
                                    setConditionalState(university, "has_back_asset", e.target.checked);
                                }}
                            />
                        }
                        label="Has Back Asset?"
                    />
                    {hasBackIcon && (
                        <Box sx={{ ml: 3, mt: 1.5, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                            <TeamImageModifier
                                label={row.label + " file"}
                                original={row.associatedRow}
                                value={(eqmConfig[university] || {})[row.associatedRow]}
                                setValue={(value) => {
                                    setEQMConfigFilter(university, value, row.associatedRow);
                                }}
                                name={`${university}-${row.associatedRow}`}
                                permissions={[views.EQMANAGER]}
                            />
                        </Box>
                    )}
                </Box>
            );
        } else if (row.name === "has_mockup_asset") {
            const hasTemplateFile = getConditionalState(university, "has_mockup_asset");
            const universityConfig = eqmConfig[university] || {};
            const currentTemplateId = (eqmConfig[university] || {})["mockup_template_general_asset_id"];
            const currentTemplateValue = getNameFromAssetId("mockup_template_general_asset_id", currentTemplateId, generalAssetByType, selectedSkuType);

            return (
                <Box sx={{ mb: 4 }}>
                    {/* <FormControlLabel
                        control={
                            <Checkbox
                                checked={true}
                                disabled
                            />
                        }
                        label="Has Template File Link?"
                    /> */}
                    <Box sx={{ ml: 3, mt: 1.5, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                        <TeamImageModifier
                            label={row.label + " file"}
                            original={row.associatedRow}
                            value={(eqmConfig[university] || {})[row.associatedRow]}
                            setValue={(value) => {
                                setEQMConfigFilter(university, value, row.associatedRow);
                            }}
                            name={`${university}-${row.associatedRow}`}
                            permissions={[views.EQMANAGER]}
                        />
                    </Box>
                </Box>
            );
        }

        return null;
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
                Asset Configuration
            </Typography>

            {errorMessage && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
                    {errorMessage}
                </Box>
            )}

            {universities.map((university) => (
                <Accordion
                    key={university}
                    expanded={expanded[university] || false}
                    onChange={handleAccordionChange(university)}
                    sx={{ mb: 2 }}
                >
                    <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
                        <Typography variant="h6">{university}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ p: 2 }}>
                            <Typography sx={{ textAlign: 'right', fontSize: '14px' }}>
                                **Only PNG files are allowed
                            </Typography>
                            {rows.map((row, index) => {
                                const fieldName = row.name

                                // Handle conditional sections
                                if (["has_front_asset", "has_back_asset", "has_mockup_asset"].includes(fieldName)) {
                                    return (
                                        <Box key={fieldName} sx={{ mb: index === fieldOrder.length - 1 ? 0 : 2 }}>
                                            {row.title && (
                                                <>
                                                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, mt: index === 0 ? 0 : 3 }}>
                                                        {row.title}
                                                    </Typography>
                                                    <Divider sx={{ mb: 2 }} />
                                                </>
                                            )}
                                            {renderConditionalSection(university, row)}
                                        </Box>
                                    );
                                }
                                return null;
                            })}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

export default NewTeamProductEQMConfig;